// src/pages/Reports.jsx
import React, { useState, useMemo } from 'react'
import { useApp } from '../context/AppContext'
import { MetricsGrid, MetricCard, ProgressRow, EmptyState, CatPill, TypeBadge } from '../components/UI'
import { CAT_COLOR } from '../utils/constants'
import { fmt, fmtDate, today } from '../utils/helpers'
import * as XLSX from 'xlsx'

const TABS = ['Summary', 'Income', 'Expenses', 'P&L']

export default function Reports() {
  const { accounts, transactions, acctName, totalIncome, totalExpense, netSavings, totalAssets, totalLiab } = useApp()
  const [tab, setTab] = useState('Summary')

  const income   = useMemo(() => transactions.filter(t => t.type === 'income'),   [transactions])
  const expenses = useMemo(() => transactions.filter(t => t.type === 'expense'),  [transactions])
  const savingsRate = totalIncome > 0 ? ((netSavings / totalIncome) * 100).toFixed(1) : 0

  const byCat = (arr) => {
    const map = {}
    arr.forEach(t => { map[t.cat] = (map[t.cat] || 0) + t.amt })
    return Object.entries(map).sort((a, b) => b[1] - a[1])
  }

  const exportExcel = () => {
    const wb = XLSX.utils.book_new()

    // Transactions sheet
    const txRows = [['Date','Ref','Type','Category','Account / Route','Amount (₹)','Note']]
    ;[...transactions].sort((a,b)=>a.date.localeCompare(b.date)).forEach(t => {
      txRows.push([t.date, t.ref, t.type, t.cat, t.type==='transfer' ? `${acctName(t.from)} → ${acctName(t.to)}` : acctName(t.acct), t.amt, t.note||''])
    })
    const ws1 = XLSX.utils.aoa_to_sheet(txRows)
    ws1['!cols'] = [{wch:12},{wch:10},{wch:10},{wch:22},{wch:32},{wch:14},{wch:36}]
    XLSX.utils.book_append_sheet(wb, ws1, 'Transactions')

    // Accounts sheet
    const aRows = [['Account','Type','Balance (₹)']]
    accounts.forEach(a => aRows.push([a.name, a.type, a.balance]))
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(aRows), 'Accounts')

    // P&L sheet
    const inByCat = {}, exByCat = {}
    income.forEach(t   => { inByCat[t.cat] = (inByCat[t.cat] || 0) + t.amt })
    expenses.forEach(t => { exByCat[t.cat] = (exByCat[t.cat] || 0) + t.amt })
    const plRows = [
      ['TecFin — Profit & Loss Statement'], [`Exported: ${today()}`], [''],
      ['INCOME',''],
      ...Object.entries(inByCat).map(([k,v]) => ['  ' + k, v]),
      ['Total Income', totalIncome], [''],
      ['EXPENSES',''],
      ...Object.entries(exByCat).map(([k,v]) => ['  ' + k, v]),
      ['Total Expenses', totalExpense], [''],
      [netSavings >= 0 ? 'NET SAVINGS' : 'NET DEFICIT', Math.abs(netSavings)],
    ]
    const ws3 = XLSX.utils.aoa_to_sheet(plRows)
    ws3['!cols'] = [{wch:28},{wch:16}]
    XLSX.utils.book_append_sheet(wb, ws3, 'P&L')

    XLSX.writeFile(wb, `TecFin_Export_${today()}.xlsx`)
  }

  return (
    <>
      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', marginBottom: 16 }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{ padding: '9px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', border: 'none', background: 'none', color: tab === t ? 'var(--brand-800)' : 'var(--gray-600)', borderBottom: `2px solid ${tab === t ? 'var(--brand-400)' : 'transparent'}`, fontFamily: 'inherit', transition: 'all .15s' }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* SUMMARY */}
      {tab === 'Summary' && (<>
        <MetricsGrid>
          <MetricCard label="Income"       value={`₹${fmt(totalIncome)}`}   color="var(--green-600)" />
          <MetricCard label="Expenses"     value={`₹${fmt(totalExpense)}`}  color="var(--red-600)" />
          <MetricCard label="Net savings"  value={`${netSavings>=0?'+':'−'}₹${fmt(Math.abs(netSavings))}`} color={netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)'} />
          <MetricCard label="Savings rate" value={`${savingsRate}%`}         color={savingsRate >= 20 ? 'var(--green-600)' : 'var(--amber-600)'} />
        </MetricsGrid>

        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-title">Net worth</div>
          {[
            { label: 'Total assets',    value: `₹${fmt(totalAssets)}`,         color: 'var(--brand-600)' },
            { label: 'Total liabilities',value: `₹${fmt(totalLiab)}`,           color: 'var(--red-600)' },
            { label: 'Net worth',       value: `₹${fmt(totalAssets - totalLiab)}`, color: totalAssets-totalLiab >= 0 ? 'var(--green-600)' : 'var(--red-600)', bold: true },
          ].map(r => (
            <div key={r.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid var(--border)', fontSize: 13, alignItems: 'center' }}>
              <span style={{ color: 'var(--gray-600)', fontWeight: r.bold ? 600 : 400 }}>{r.label}</span>
              <span style={{ color: r.color, fontWeight: 600 }}>{r.value}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div className="card-title">Account balances</div>
          {accounts.map(a => {
            const pct = Math.min(Math.abs(a.balance) / (totalAssets + totalLiab || 1) * 100, 100)
            return (
              <div key={a.id} style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 10, height: 10, borderRadius: '50%', background: a.color, display: 'inline-block' }} />
                    {a.name}
                  </span>
                  <span style={{ fontWeight: 600, color: a.balance >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>₹{fmt(Math.abs(a.balance))}</span>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${pct.toFixed(1)}%`, background: a.color }} />
                </div>
              </div>
            )
          })}
        </div>
      </>)}

      {/* INCOME */}
      {tab === 'Income' && (<>
        <MetricsGrid>
          <MetricCard label="Total income" value={`₹${fmt(totalIncome)}`}  color="var(--green-600)" />
          <MetricCard label="Transactions" value={String(income.length)}    color="var(--gray-800)" />
          <MetricCard label="Avg per txn"  value={`₹${fmt(income.length ? totalIncome / income.length : 0)}`} color="var(--green-600)" />
        </MetricsGrid>
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-title">By category</div>
          {byCat(income).map(([cat, amt]) => (
            <ProgressRow key={cat} label={cat} amount={fmt(amt)} pct={amt / totalIncome * 100} color={CAT_COLOR[cat] || 'var(--green-600)'} />
          ))}
          {income.length === 0 && <EmptyState icon="ti-trending-up" message="No income transactions" />}
        </div>
        <div className="table-wrap">
          <div className="table-toolbar"><span className="table-toolbar-title">Income transactions ({income.length})</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead><tr><th style={{width:90}}>Date</th><th style={{width:72}}>Ref</th><th style={{width:130}}>Category</th><th>Note</th><th style={{width:105,textAlign:'right'}}>Amount</th></tr></thead>
              <tbody>
                {income.sort((a,b)=>b.date.localeCompare(a.date)).map(t => (
                  <tr key={t.id}>
                    <td className="mono">{fmtDate(t.date)}</td>
                    <td className="mono">{t.ref}</td>
                    <td><CatPill cat={t.cat} /></td>
                    <td style={{ color: 'var(--gray-600)' }}>{t.note}</td>
                    <td style={{ textAlign: 'right' }} className="c-income">+₹{fmt(t.amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* EXPENSES */}
      {tab === 'Expenses' && (<>
        <MetricsGrid>
          <MetricCard label="Total expenses" value={`₹${fmt(totalExpense)}`}  color="var(--red-600)" />
          <MetricCard label="Transactions"   value={String(expenses.length)}   color="var(--gray-800)" />
          <MetricCard label="Avg per txn"    value={`₹${fmt(expenses.length ? totalExpense / expenses.length : 0)}`} color="var(--red-600)" />
          <MetricCard label="Categories"     value={String(new Set(expenses.map(t => t.cat)).size)} color="var(--gray-800)" />
        </MetricsGrid>
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-title">Expense breakdown</div>
          {byCat(expenses).map(([cat, amt]) => (
            <ProgressRow key={cat} label={cat} amount={fmt(amt)} pct={amt / totalExpense * 100} color={CAT_COLOR[cat] || 'var(--red-600)'} />
          ))}
          {expenses.length === 0 && <EmptyState icon="ti-trending-down" message="No expense transactions" />}
        </div>
        <div className="table-wrap">
          <div className="table-toolbar"><span className="table-toolbar-title">Expense transactions ({expenses.length})</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead><tr><th style={{width:90}}>Date</th><th style={{width:72}}>Ref</th><th style={{width:130}}>Category</th><th>Note</th><th style={{width:105,textAlign:'right'}}>Amount</th></tr></thead>
              <tbody>
                {expenses.sort((a,b)=>b.date.localeCompare(a.date)).map(t => (
                  <tr key={t.id}>
                    <td className="mono">{fmtDate(t.date)}</td>
                    <td className="mono">{t.ref}</td>
                    <td><CatPill cat={t.cat} /></td>
                    <td style={{ color: 'var(--gray-600)' }}>{t.note}</td>
                    <td style={{ textAlign: 'right' }} className="c-expense">−₹{fmt(t.amt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>)}

      {/* P&L */}
      {tab === 'P&L' && (<>
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ padding: '10px 15px', background: 'var(--green-50)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--green-600)', textTransform: 'uppercase', letterSpacing: .5 }}>Income</span>
          </div>
          {byCat(income).map(([cat, amt]) => (
            <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--gray-600)' }}>{cat}</span>
              <span className="c-income">₹{fmt(amt)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', fontSize: 13, fontWeight: 700, borderBottom: '1px solid var(--border-strong)' }}>
            <span>Total income</span><span className="c-income">₹{fmt(totalIncome)}</span>
          </div>

          <div style={{ padding: '10px 15px', background: 'var(--red-50)', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--red-600)', textTransform: 'uppercase', letterSpacing: .5 }}>Expenses</span>
          </div>
          {byCat(expenses).map(([cat, amt]) => (
            <div key={cat} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
              <span style={{ color: 'var(--gray-600)' }}>{cat}</span>
              <span className="c-expense">₹{fmt(amt)}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 15px', fontSize: 13, fontWeight: 700, borderBottom: '1px solid var(--border-strong)' }}>
            <span>Total expenses</span><span className="c-expense">₹{fmt(totalExpense)}</span>
          </div>

          <div style={{ padding: '11px 15px', background: netSavings >= 0 ? 'var(--brand-50)' : 'var(--red-50)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 700 }}>
              <span style={{ color: netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>
                {netSavings >= 0 ? 'Net savings' : 'Net deficit'}
              </span>
              <span style={{ color: netSavings >= 0 ? 'var(--brand-600)' : 'var(--red-600)' }}>
                {netSavings >= 0 ? '+' : '−'}₹{fmt(Math.abs(netSavings))}
              </span>
            </div>
          </div>
        </div>

        <button className="btn btn-success" onClick={exportExcel} style={{ marginBottom: 8 }}>
          <i className="ti ti-file-spreadsheet" aria-hidden="true" />
          Export to Excel
        </button>
      </>)}
    </>
  )
}
